import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { useUpdateChatBotSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateChatBotSettings";
import { haveChangedLoaderSettings } from "@jugl-web/domain-resources/chat-bot/utils/haveChangedLoaderSettings";
import { transformChatBotSettingsToFormValues } from "@jugl-web/domain-resources/chat-bot/utils/transformChatBotSettingsToFormValues";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { MultiSectionLayout, Tab } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { SHOW_CHAT_BOT_LOADER_SETTINGS_UPDATED_ALERT_KEY } from "@jugl-web/utils/storage";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { RepasteSnippetCodeAlert } from "@web-src/modules/cx/components/RepasteSnippetCodeAlert";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { AppearanceCTATab } from "./components/AppearanceCTATab";
import { AppearanceGeneralTab } from "./components/AppearanceGeneralTab";
import { AppearanceIconTab } from "./components/AppearanceIconTab";
import { AppearanceSuggestionsTab } from "./components/AppearanceSuggestionsTab";
import { ChatBotChannelPreview } from "./components/ChatBotChannelPreview";
import { ChatbotPreviewBackground } from "./components/ChatbotPreviewBackground";

const DEFAULT_PREVIEW_CTAS = [
  "Hi! 👋 Have some questions regarding pricing?",
  "AI Bot is here around the clock to help you 🙌",
];

const DEFAULT_PREVIEW_SUGGESTIONS = [
  "What do you do?",
  "Place an order",
  "Book a demo",
  "Schedule a call",
  "Get help",
];

export const WebChatBotAppearanceTab: FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();

  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();

  const [activeTab, setActiveTab] = useState<
    "general" | "icon" | "cta" | "suggestions"
  >("general");

  const { webChatBotAccount } = useWebChatBotAccount();

  const { updateChatBotSettings } = useUpdateChatBotSettings({ entityId });

  const [, setWasLoaderSettingsUpdated] = useLocalStorage(
    SHOW_CHAT_BOT_LOADER_SETTINGS_UPDATED_ALERT_KEY,
    false
  );

  const [isRepasteSnippetCodeAlertOpen, setIsRepasteSnippetCodeAlertOpen] =
    useState(false);

  const form = useForm<ChatBotSettingsFormValues>();

  const { handleSubmit, reset, control, formState } = form;

  const resetForm = useCallback(() => {
    if (!webChatBotAccount) {
      return;
    }

    reset(transformChatBotSettingsToFormValues(webChatBotAccount.settings));
  }, [reset, webChatBotAccount]);

  const watchedValues = useWatch({ control });

  const previewCtas = useMemo(() => {
    const sourceCtas =
      (watchedValues.ctas as { id: string; value: string }[]) ?? [];

    const nonEmptyCtas = sourceCtas.filter(
      (cta) => cta.value.trim().length > 0
    );

    if (nonEmptyCtas.length > 0) {
      return nonEmptyCtas;
    }

    if (watchedValues.ctaVisualEnabled) {
      return [];
    }

    // If no CTAs or no CTA visual is set, show example CTAs
    return DEFAULT_PREVIEW_CTAS.map((value, index) => ({
      id: `default-cta-${index}`,
      value,
    }));
  }, [watchedValues.ctas, watchedValues.ctaVisualEnabled]);

  const previewSuggestions = useMemo(() => {
    const sourceSuggestions =
      (watchedValues.suggestions as { id: string; value: string }[]) ?? [];

    const nonEmptySuggestions = sourceSuggestions.filter(
      (suggestion) => suggestion.value.trim().length > 0
    );

    if (nonEmptySuggestions.length > 0) {
      return nonEmptySuggestions;
    }

    return DEFAULT_PREVIEW_SUGGESTIONS.map((value, index) => ({
      id: `default-suggestion-${index}`,
      value,
    }));
  }, [watchedValues.suggestions]);

  const handleSaveChanges = useCallback(
    async (formData: ChatBotSettingsFormValues) => {
      const wasLoaderSettingsUpdated = haveChangedLoaderSettings(formState);

      const removeEmptyItems = (
        items: { id: string; value: string }[]
      ): string[] =>
        items.map((item) => item.value.trim()).filter((v) => v.length > 0);

      try {
        await updateChatBotSettings({
          welcomeMessage: formData.welcomeMessage.trim(),
          agentName: formData.agentName.trim(),
          agentImage: formData.agentImage,
          chatName: formData.chatName.trim(),
          chatImage: formData.chatImage,
          buttonIcon: formData.buttonIcon,
          buttonColor: formData.buttonColor,
          senderMessageBackgroundColor: formData.senderMessageBackgroundColor,
          theme: formData.theme,
          alignment: formData.alignment,
          bottomSpacing: formData.bottomSpacing,
          sideSpacing: formData.sideSpacing,
          ctas: removeEmptyItems(formData.ctas),
          ctaVisual: formData.ctaVisualEnabled ? formData.ctaVisual : null,
          suggestions: removeEmptyItems(formData.suggestions),
        });

        toast(
          t({
            id: "feedback.web-chat-settings-saved",
            defaultMessage: "Web Chat settings have been updated",
          })
        );

        if (wasLoaderSettingsUpdated) {
          setWasLoaderSettingsUpdated(true);
          setIsRepasteSnippetCodeAlertOpen(true);
        }
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [formState, setWasLoaderSettingsUpdated, t, toast, updateChatBotSettings]
  );

  const tabs: { id: typeof activeTab; title: string }[] = useMemo(
    () => [
      {
        id: "general",
        title: t({
          id: "client-support-page.appearance-tab-general",
          defaultMessage: "General",
        }),
      },
      {
        id: "icon",
        title: t({
          id: "client-support-page.appearance-tab-icon",
          defaultMessage: "Icon",
        }),
      },
      {
        id: "cta",
        title: t({
          id: "client-support-page.appearance-tab-cta",
          defaultMessage: "CTA",
        }),
      },
      {
        id: "suggestions",
        title: t({
          id: "client-support-page.appearance-tab-suggestions",
          defaultMessage: "Suggestions",
        }),
      },
    ],
    [t]
  );

  // Reset the form when the component mounts/settings change
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    return registerDirtySource(
      "web-chat-bot-appearance-tab-form",
      formState.isDirty
    );
  }, [formState.isDirty, registerDirtySource]);

  return (
    <>
      <MultiSectionLayout
        classNames={{
          content: "flex items-center justify-center",
        }}
        leftSide={
          <MultiSectionLayout
            classNames={{
              content:
                "border-grey-300 flex h-full w-[464px] shrink-0 flex-col border-0 border-r border-solid bg-white",
            }}
            footer={
              formState.isDirty && (
                <div className="border-grey-300 flex gap-4 border-l-0 border-r border-t border-b-0 border-solid bg-white px-8 pt-4 pb-6">
                  <Button
                    variant="tertiary"
                    size="md"
                    className="flex-1"
                    onClick={() => requestNavigation(resetForm)}
                  >
                    {t({
                      id: "common.cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    className="flex-[2_1_0%]"
                    onClick={handleSubmit(handleSaveChanges)}
                    iconEnd={<ArrowRightIcon />}
                  >
                    {t({
                      id: "common.save-changes",
                      defaultMessage: "Save changes",
                    })}
                  </Button>
                </div>
              )
            }
          >
            <div className="border-grey-300 flex h-[70px] shrink-0 items-end gap-1 border-0 border-b border-solid px-8">
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  title={tab.title}
                  isSelected={tab.id === activeTab}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </div>
            <FormProvider {...form}>
              <div className="jugl__custom-scrollbar flex h-full flex-col overflow-y-auto px-6 py-10">
                {activeTab === "general" && <AppearanceGeneralTab />}
                {activeTab === "icon" && <AppearanceIconTab />}
                {activeTab === "cta" && <AppearanceCTATab />}
                {activeTab === "suggestions" && <AppearanceSuggestionsTab />}
              </div>
            </FormProvider>
          </MultiSectionLayout>
        }
      >
        <ChatbotPreviewBackground>
          <ChatBotChannelPreview
            entityId={entityId}
            chatName={watchedValues.chatName ?? "Support"}
            agentName={watchedValues.agentName ?? "Agent"}
            welcomeMessage={
              watchedValues.welcomeMessage ?? "Hello! How can I help you?"
            }
            senderMessageBackgroundColor={
              watchedValues.senderMessageBackgroundColor ?? "#1A75D2"
            }
            theme={watchedValues.theme ?? "light"}
            buttonColor={watchedValues.buttonColor ?? "#1A75D2"}
            alignment={watchedValues.alignment ?? "right"}
            bottomSpacing={watchedValues.bottomSpacing ?? 20}
            sideSpacing={watchedValues.sideSpacing ?? 20}
            chatImage={watchedValues.chatImage ?? ""}
            agentImage={watchedValues.agentImage ?? ""}
            buttonIcon={watchedValues.buttonIcon ?? ""}
            ctaVisual={
              watchedValues.ctaVisualEnabled
                ? watchedValues.ctaVisual ?? null
                : null
            }
            ctas={previewCtas}
            suggestions={previewSuggestions}
            chatMode="mocked"
            previewMode={(() => {
              if (activeTab === "icon") return "launcher";
              if (activeTab === "cta") return "ctas";
              if (activeTab === "suggestions") return "suggestions";
              return "default";
            })()}
          />
        </ChatbotPreviewBackground>
      </MultiSectionLayout>
      <RepasteSnippetCodeAlert
        isOpen={isRepasteSnippetCodeAlertOpen}
        onClose={() => setIsRepasteSnippetCodeAlertOpen(false)}
      />
    </>
  );
};
